import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from './client';

export type Me = Schemas['Me'];
export type UserPreferences = Schemas['UserPreferences'];

export const sessionKey = ['session'] as const;

export function useSession() {
  return useQuery({
    queryKey: sessionKey,
    queryFn: () => unwrap(api.GET('/api/v1/auth/session')),
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { email: string; password: string }) => unwrap(api.POST('/api/v1/auth/login', { body })),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['RegisterRequest']) => unwrap(api.POST('/api/v1/auth/register', { body })),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/auth/logout')),
    onSuccess: () => {
      qc.clear();
      // Zwischengespeicherte API-Antworten (Offline-Cache) gehören dem abgemeldeten Benutzer
      if (typeof caches !== 'undefined') void caches.delete('api');
    },
  });
}

export function useUpdateMe() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Schemas['UpdateProfileRequest']) => unwrap(api.PATCH('/api/v1/me', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (body: Schemas['ChangePasswordRequest']) => unwrap(api.POST('/api/v1/me/password', { body })),
  });
}

export function useStopImpersonation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/v1/auth/impersonation/stop')),
    onSuccess: () => qc.invalidateQueries(),
  });
}
